import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Doctor-level PDF customization layer.
 *
 * Sits on top of the clinic's branding (logo, name, primary colour) which
 * stays locked to the tenant. A provider may pick a different document
 * TEMPLATE, nudge the ACCENT colour, save a SIGNATURE image, and set a
 * CREDENTIALS line for their signature block. The PDF renderer
 * (src/utils/pdfBrand.ts → refreshBrandAssets) merges these over the clinic
 * defaults at draw time: doctor → clinic → tenant default.
 *
 * Persisted to localStorage today so it works per-device with no backend.
 * INTEGRATION POINT: when /users/me/pdf-prefs lands, hydrate this store from
 * the API after auth and persist changes back (same pattern as voice-prefs /
 * a11y prefs) so preferences follow the doctor across devices.
 */

export type PdfTemplate = 'editorial' | 'letterhead' | 'compact' | 'minimal' | 'preprinted';

export const PDF_TEMPLATE_LABELS: Record<PdfTemplate, string> = {
  editorial: 'Editorial',
  letterhead: 'Classic letterhead',
  compact: 'Compact',
  minimal: 'Minimal mono',
  preprinted: 'Pre-printed paper',
};

export interface PdfPrefs {
  /** Override the clinic's default document template. */
  template?: PdfTemplate;
  /** Override the accent colour (hex). Clinic logo + name stay locked. */
  accentColor?: string;
  /** Saved signature image (base64 PNG) used on Rx / referrals. */
  signatureImage?: string;
  /** Free-text credentials shown under the provider name (e.g. "MD, FRCS"). */
  credentialsLine?: string;
}

interface PdfPrefsStore extends PdfPrefs {
  setPdfPrefs: (prefs: Partial<PdfPrefs>) => void;
  /** Replace the whole prefs object (e.g. when hydrating from the API). */
  hydratePdfPrefs: (prefs: PdfPrefs) => void;
  resetPdfPrefs: () => void;
}

const EMPTY: PdfPrefs = {
  template: undefined,
  accentColor: undefined,
  signatureImage: undefined,
  credentialsLine: undefined,
};

export const usePdfPrefsStore = create<PdfPrefsStore>()(
  persist(
    (set) => ({
      ...EMPTY,
      setPdfPrefs: (prefs) => set((s) => ({ ...s, ...prefs })),
      hydratePdfPrefs: (prefs) => set((s) => ({ ...s, ...EMPTY, ...prefs })),
      resetPdfPrefs: () => set((s) => ({ ...s, ...EMPTY })),
    }),
    { name: 'maie-pdf-prefs' },
  ),
);
